import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { KorviuButtonComponent, KorviuLoaderComponent } from 'shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ProductCardComponent } from 'shared/components/product-card/product-card.component';
import { CategoriesService } from 'core';
import { catchError, EMPTY, finalize, Observable, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IProduct } from 'core/types/product.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';

@Component({
  selector: 'app-favorites',
  imports: [KorviuButtonComponent, TranslatePipe, ProductCardComponent, KorviuLoaderComponent],
  templateUrl: './favorites.component.html',
  styleUrl: './favorites.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FavoritesComponent implements OnInit {
  readonly #categoriesService = inject(CategoriesService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #translateService = inject(TranslateService);

  readonly favorites = signal<IProduct[]>([]);
  readonly favoritesIsLoading = signal(false);
  readonly favoritesHasError = signal(false);
  readonly suggested = signal<IProduct[]>([]);
  readonly suggestedIsLoading = signal(false);
  readonly suggestedHasError = signal(false);
  readonly currentLang = signal(this.#translateService.currentLang);
  readonly isEnglish = computed(() => this.currentLang() === 'en');

  constructor() {
    this.#translateService.onLangChange
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(event => this.currentLang.set(event.lang));
  }

  ngOnInit(): void {
    this.#loadFavorites();
  }

  onExploreClick(): void {
    void this.#router.navigate(['explore'], { relativeTo: this.#activatedRoute.parent });
  }

  onRetryFavoritesClick(): void {
    this.#loadFavorites();
  }

  onRetrySuggestedClick(): void {
    this.#loadSuggestedProducts().pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
  }

  #loadFavorites(): void {
    this.favorites.set([]);
    this.favoritesIsLoading.set(true);
    this.favoritesHasError.set(false);
    this.suggested.set([]);
    this.suggestedHasError.set(false);

    this.#categoriesService
      .getFavoriteProducts({ page: 1, pageSize: 12 })
      .pipe(
        tap(res => {
          this.favorites.set(res.results ?? []);
        }),
        catchError(() => {
          this.favoritesHasError.set(true);

          return of(null);
        }),
        finalize(() => {
          this.favoritesIsLoading.set(false);
        }),
        switchMap(res => (res && !res.results?.length ? this.#loadSuggestedProducts() : EMPTY)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #loadSuggestedProducts(): Observable<IGetPaginatedResponse<IProduct[]>> {
    this.suggested.set([]);
    this.suggestedIsLoading.set(true);
    this.suggestedHasError.set(false);

    return this.#categoriesService.getFeaturedProducts({ page: 1, pageSize: 4 }).pipe(
      tap(res => {
        this.suggested.set(res.results ?? []);
      }),
      catchError(() => {
        this.suggestedHasError.set(true);

        return EMPTY;
      }),
      finalize(() => {
        this.suggestedIsLoading.set(false);
      })
    );
  }
}
